import React, { useState, useEffect, useRef, useContext } from "react"; // Added useContext
import { fakerEN_AU as faker } from "@faker-js/faker";
import nlp from 'compromise';
// Import FontAwesome icons
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEdit, faTrashAlt, faArrowCircleRight, faSyncAlt, faTimes, faInfoCircle, faCog, faSun, faMoon } from "@fortawesome/free-solid-svg-icons"; // Added faSun, faMoon
import "./App.css"; // Use CSS for additional styling
import { ThemeContext } from './ThemeContext'; // Import ThemeContext

const PlaceholderTool = () => {
  const { theme, toggleTheme } = useContext(ThemeContext); // Consume ThemeContext

  const [placeholderMap, setPlaceholderMap] = useState(() => {
    const storedPlaceholderMap = localStorage.getItem("placeholderMap");
    return storedPlaceholderMap ? JSON.parse(storedPlaceholderMap) : {};
  });
  const [nonWordWhiteList, setNonWordWhiteList] = useState(() => {
    const storedWhiteList = localStorage.getItem("nonWordWhiteList");
    return storedWhiteList ? JSON.parse(storedWhiteList) : [];
  });
  const [originalText, setOriginalText] = useState("");
  const [convertedText, setConvertedText] = useState("");
  const [customPlaceholder, setCustomPlaceholder] = useState("");
  const [customOriginalValue, setCustomOriginalValue] = useState("");
  const [editKey, setEditKey] = useState(null); // Currently edited original text value
  const [editedValue, setEditedValue] = useState(""); // Edited placeholder value
  const [isInfoModalOpen, setIsInfoModalOpen] = useState(false); // New state for info modal
  const [anonymizePhoneNumberType, setAnonymizePhoneNumberType] = useState(() => {
    const storedValue = localStorage.getItem("anonymizePhoneNumberType");
    return storedValue ? storedValue : 'placeholder';
  });
  const [anonymizeEmailType, setAnonymizeEmailType] = useState(() => {
    const storedValue = localStorage.getItem("anonymizeEmailType");
    if (storedValue === 'domainOnly') {
      // Migrate old value. localStorage will be updated by useEffect on next save.
      return 'redactFullEmail';
    }
    return storedValue ? storedValue : 'placeholder';
  });
  const [anonymizeNameType, setAnonymizeNameType] = useState(() => {
    const storedValue = localStorage.getItem("anonymizeNameType");
    return storedValue ? storedValue : 'placeholder';
  });
  const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false);
  

  const fileInputRef = useRef(null);

  // Effect for showing info modal on first visit
  useEffect(() => {
    const hasSeenInfoModal = localStorage.getItem("hasSeenInfoModal");
    if (!hasSeenInfoModal) {
      setIsInfoModalOpen(true);
      localStorage.setItem("hasSeenInfoModal", "true");
    }
  }, []);

  // Effect to apply theme class to body
  useEffect(() => {
    document.body.classList.remove('light-mode', 'dark-mode'); // Clear existing theme classes
    document.body.classList.add(theme === 'dark' ? 'dark-mode' : 'light-mode');
  }, [theme]);

  // Persist placeholderMap and whitelist to localStorage
  useEffect(() => {
    try {
      localStorage.setItem("placeholderMap", JSON.stringify(placeholderMap));
      localStorage.setItem("nonWordWhiteList", JSON.stringify(nonWordWhiteList));
      localStorage.setItem("anonymizePhoneNumberType", anonymizePhoneNumberType);
      localStorage.setItem("anonymizeEmailType", anonymizeEmailType);
      localStorage.setItem("anonymizeNameType", anonymizeNameType);
    } catch (error) {
      console.error("Failed to save to localStorage:", error);
    }
  }, [placeholderMap, nonWordWhiteList, anonymizePhoneNumberType, anonymizeEmailType, anonymizeNameType]);

  // Shared regex patterns for names, emails, phone numbers, and other formats
  const patterns = {
    name: /\b[A-Z][a-z]+ [A-Z][a-z]+\b/g, // Matches "John Doe"
    email: /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[a-z]{2,}\b/gi, // Matches "test@example.com"
    phoneNumber: /\b\d{4} ?\d{3} ?\d{3}\b/g, // Matches phone numbers like "0408 741 926" or "0408741926"
    company: /\b[A-Z][a-z]+\b/g, // Matches "Google", "Apple"
    acronym: /^[A-Z]+$/g, // Matches "NASA", "HTML" (All caps/acronym format)
  };

  // Guess type
  const guessType = (string) => {
    let type = "";
    patterns.email.lastIndex = 0;
    if (patterns.email.test(string)) {
      type = "email";
    }
    patterns.phoneNumber.lastIndex = 0;
    if (patterns.phoneNumber.test(string)) {
      type = "phone";
    }
    patterns.company.lastIndex = 0;
    if (patterns.company.test(string)) {
      type = "company";
    }
    patterns.acronym.lastIndex = 0;
    if (patterns.acronym.test(string)) {
      type = "acronym";
    }
    patterns.name.lastIndex = 0;
    if (patterns.name.test(string) || nlp(string).has("#Person")) {
      type = "name";
    }
    return type;
  };

  /**
   * Generate a placeholder based on the specified type.
   * @param {string} original - The original text to base the placeholder on.
   * @param {string} type - The type of text (e.g., 'name', 'company', 'email', 'phone', 'acronym', or 'other').
   */
  const generatePlaceholder = (original, type) => {
    switch (type) {
      case "name":
        return faker.person.firstName() + " " + faker.person.lastName();
      case "company":
        return faker.company.name();
      case "acronym":
        return faker.string.alpha({ length: original.length }).toUpperCase();
      case "email":
        return faker.internet.email();
      case "phone":
        return faker.phone.number({ style: "national" });
      default:
        return faker.lorem.word();
    }
  };

  const handleConvert = () => {
    const newPlaceholderMap = { ...placeholderMap };
    let result = originalText;
    // Handle name replacements
    result = result.replace(patterns.name, (match) => {
      if (nonWordWhiteList.includes(match)) return match; // Skip non-name whitelist items
      // Doesn't look like a name
      if (
        !nlp(match).has("#Person") ||
        nlp(match).has("#Expression") ||
        nlp(match).match("#ProperNoun").out("array").length < 2
      ) {
        return match;
      }
      // Check if the original value already has a placeholder
      if (newPlaceholderMap[match]) return newPlaceholderMap[match];

      if (anonymizeNameType === 'firstNameInitial') {
        const parts = match.split(' ');
        let anonymizedName = parts[0]; // First name
        if (parts.length > 1) {
          anonymizedName += ` ${parts[parts.length - 1].charAt(0)}.`; // Last initial
        }
        newPlaceholderMap[match] = anonymizedName;
        return anonymizedName;
      } else {
        // Default to generating a faker placeholder
        const placeholder = generatePlaceholder(match, "name"); // Pass type as "name"
        newPlaceholderMap[match] = placeholder;
        return placeholder;
      }
    });
    // Handle email replacements
    result = result.replace(patterns.email, (match) => {
      if (newPlaceholderMap[match]) return newPlaceholderMap[match]; // Check if already processed

      if (anonymizeEmailType === 'redactFullEmail') {
        const anonymizedEmail = "[email protected]";
        newPlaceholderMap[match] = anonymizedEmail;
        return anonymizedEmail;
      } else {
        // Default to generating a faker placeholder
        const placeholder = generatePlaceholder(match, "email"); // Pass type as "email"
        newPlaceholderMap[match] = placeholder;
        return placeholder;
      }
    });
    // Handle phone number replacements
    result = result.replace(patterns.phoneNumber, (match) => {
      if (newPlaceholderMap[match]) return newPlaceholderMap[match];

      if (anonymizePhoneNumberType === 'lastThree') {
        const lastThreeDigits = match.replace(/\s|-/g, "").slice(-3);
        const anonymizedPhoneNumber = `xxxx-xxx-${lastThreeDigits}`;
        newPlaceholderMap[match] = anonymizedPhoneNumber;
        return anonymizedPhoneNumber;
      } else {
        const placeholder = generatePlaceholder(match, "phone"); // Pass type as "phone"
        newPlaceholderMap[match] = placeholder;
        return placeholder;
      }
    });
    // Run through all the mappings to catch anything that doesn't have a "type"
    Object.keys(newPlaceholderMap).forEach((original) => {
      const regex = new RegExp(
        original.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"),
        "g"
      );
      result = result.replace(regex, newPlaceholderMap[original]);
    });
    setPlaceholderMap(newPlaceholderMap);
    setConvertedText(result);
  };

  const handleReverse = () => {
    let result = convertedText;
    for (const [original, placeholder] of Object.entries(placeholderMap)) {
      const regex = new RegExp(placeholder.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"), "g");
      result = result.replace(regex, original);
    }
    setOriginalText(result);
  };

  const handleRemovePlaceholder = (originalValue) => {
    const updatedPlaceholderMap = { ...placeholderMap };
    delete updatedPlaceholderMap[originalValue];
    setPlaceholderMap(updatedPlaceholderMap);
  };

  const handleConvertToWhitelist = (originalValue) => {
    // Add original value to the whitelist
    setNonWordWhiteList((prevWhitelist) => [...prevWhitelist, originalValue]);
    // Remove from placeholderMap
    handleRemovePlaceholder(originalValue);
  };

  const handleEditPlaceholder = (originalValue) => {
    setEditKey(originalValue);
    setEditedValue(placeholderMap[originalValue]);
  };

  const saveEditedPlaceholder = () => {
    if (!editedValue.trim()) {
      alert("Placeholder cannot be empty.");
      return;
    }
    // Check for duplicate placeholders
    const duplicate = Object.values(placeholderMap).find(
      (value) => value === editedValue && editedValue !== placeholderMap[editKey]
    );
    if (duplicate) {
      alert("A placeholder with this value already exists.");
      return;
    }
    const updatedPlaceholderMap = { ...placeholderMap };
    updatedPlaceholderMap[editKey] = editedValue;
    setPlaceholderMap(updatedPlaceholderMap);
    setEditKey(null);
    setEditedValue("");
  };

  const handleRemoveFromWhitelist = (item) => {
    setNonWordWhiteList((prevWhitelist) => prevWhitelist.filter((word) => word !== item));
  };

  const addCustomMapping = () => {
    let originalValue = customOriginalValue.trim();
    if (!originalValue) {
      alert("Original value is required.");
      return;
    }
    if (placeholderMap[originalValue]) {
      alert(originalValue + " already has a placeholder");
      return;
    }
    const placeholder =
      customPlaceholder.trim() || generatePlaceholder(originalValue, guessType(originalValue));
    if (Object.values(placeholderMap).includes(placeholder)) {
      alert("A placeholder with this value already exists.");
      return;
    }
    setPlaceholderMap((prevMap) => ({
      ...prevMap,
      [originalValue]: placeholder,
    }));
    setCustomPlaceholder("");
    setCustomOriginalValue("");
  };

  const regeneratePlaceholder = (originalValue) => {
    const type = guessType(originalValue);
    // Generate a new placeholder based on original value format
    let newPlaceholder = generatePlaceholder(originalValue, type);
    while (Object.values(placeholderMap).includes(newPlaceholder)) {
      newPlaceholder = generatePlaceholder(originalValue, type); // Ensure uniqueness
    }
    // Update the placeholderMap
    const updatedPlaceholderMap = { ...placeholderMap };
    updatedPlaceholderMap[originalValue] = newPlaceholder;
    setPlaceholderMap(updatedPlaceholderMap);
  };

  // Updated export function to include nonWordWhiteList
  const handleExportSettings = () => {
    const data = {
      placeholderMap,
      nonWordWhiteList,
      anonymizePhoneNumberType,
      anonymizeEmailType,
      anonymizeNameType,
    };
    const dataStr = JSON.stringify(data, null, 2);
    const blob = new Blob([dataStr], { type: "application/json" });
    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.download = "settings.json";
    link.click();

    URL.revokeObjectURL(url);
  };

  const handleImportSettings = () => {
    fileInputRef.current.click();
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const importedData = JSON.parse(e.target.result);
        // Check if importedData has the correct structure
        if (
          typeof importedData !== "object" ||
          importedData === null ||
          !("placeholderMap" in importedData) ||
          !("nonWordWhiteList" in importedData)
        ) {
          throw new Error("Invalid JSON structure.");
        }
        // Merge with existing placeholderMap and nonWordWhiteList, overwriting conflicts
        setPlaceholderMap((prevMap) => ({
          ...prevMap,
          ...importedData.placeholderMap,
        }));
        setNonWordWhiteList((prevList) => Array.from(new Set([...prevList, ...importedData.nonWordWhiteList])));
        
        // Import anonymization settings, defaulting if not present
        setAnonymizePhoneNumberType(importedData.anonymizePhoneNumberType || 'placeholder');
        
        const importedEmailType = importedData.anonymizeEmailType;
        if (importedEmailType === 'domainOnly') {
          setAnonymizeEmailType('redactFullEmail');
        } else if (importedEmailType) {
          setAnonymizeEmailType(importedEmailType);
        } else {
          setAnonymizeEmailType('placeholder');
        }
        
        setAnonymizeNameType(importedData.anonymizeNameType || 'placeholder');
        
        alert("Settings imported successfully.");
      } catch (error) {
        console.error("Error parsing JSON:", error);
        alert("Failed to import settings. Invalid JSON file.");
      }
    };
    reader.readAsText(file);
    // Reset the file input
    event.target.value = null;
  };

  return (
    <div className="container">
      <div className="app-header-controls">
        <h1>DecoyText</h1>
        <div className="icon-buttons">
          <FontAwesomeIcon icon={faInfoCircle} onClick={() => setIsInfoModalOpen(true)} className="header-icon" title="More Information" />
          <FontAwesomeIcon icon={faCog} onClick={() => setIsSettingsModalOpen(true)} className="header-icon" title="Settings" />
          <FontAwesomeIcon 
            icon={theme === 'light' ? faMoon : faSun} 
            onClick={toggleTheme} 
            className="header-icon" 
            title={theme === 'light' ? 'Switch to Dark Mode' : 'Switch to Light Mode'} 
          />
        </div>
      </div>
      
      {/* Textareas Section */}
      <div className="text-inputs">
        {/* Original Textarea */}
        <div className="text-area">
          <label htmlFor="originalText" style={{ marginBottom: "5px", fontWeight: "bold" }}>
            Original Text
          </label>
          <textarea
            id="originalText"
            value={originalText}
            onChange={(e) => setOriginalText(e.target.value)}
            placeholder="Enter the original text here..."
            style={{
              width: "100%",
              height: "200px",
              fontSize: "1rem",
              padding: "10px",
              resize: "vertical",
            }}
          ></textarea>
        </div>

        {/* Convert Button */}
        <button onClick={handleConvert} style={{ margin: "10px 0" }}>
          Convert Placeholders
        </button>

        {/* Converted Textarea */}
        <div className="text-area">
          <label htmlFor="convertedText" style={{ marginBottom: "5px", fontWeight: "bold" }}>
            Converted Text
          </label>
          <textarea
            id="convertedText"
            value={convertedText}
            onChange={(e) => setConvertedText(e.target.value)}
            placeholder="Converted text with placeholders will appear here..."
            style={{
              width: "100%",
              height: "200px",
              fontSize: "1rem",
              padding: "10px",
              resize: "vertical",
            }}
          ></textarea>
        </div>

        {/* Reverse Placeholders Button */}
        <button onClick={handleReverse} style={{ marginTop: "10px" }}>
          Reverse Placeholders
        </button>
      </div>
      {/* Divider */}
      <hr style={{ border: "1px solid #ddd", margin: "20px 0" }} />
      {/* Add Custom Mapping */}
      <h2>Add Custom Mapping</h2>
      <div className="input-group" style={{ display: "flex", gap: "10px", marginBottom: "20px" }}>
        <input
          type="text"
          placeholder="Original Value"
          value={customOriginalValue}
          onChange={(e) => setCustomOriginalValue(e.target.value)}
          style={{ flex: "1" }}
        />
        <input
          type="text"
          placeholder="Add Optional Placeholder"
          value={customPlaceholder}
          onChange={(e) => setCustomPlaceholder(e.target.value)}
          style={{ flex: "1" }}
        />
        <button onClick={addCustomMapping}>Add</button>
      </div>
      {/* Placeholder Mapping */}
      <h2>Placeholder Mapping</h2>
      {Object.keys(placeholderMap).length === 0 ? (
        <p>No placeholder mappings available.</p>
      ) : (
        <ul style={{ listStyleType: "none", padding: "0" }}>
          {Object.entries(placeholderMap)
            .sort(([aOriginal], [bOriginal]) => aOriginal.localeCompare(bOriginal)) // Sort by original values
            .map(([original, placeholder]) => (
              <li
                key={original}
                className="list-item"
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  padding: "8px",
                  borderBottom: "1px solid #ddd",
                }}
              >
                {editKey === original ? (
                  <>
                    <div style={{ flex: "1" }}>
                      <strong>Original:</strong> {original}
                      <br />
                      <strong>Placeholder:</strong>{" "}
                      <input
                        type="text"
                        value={editedValue}
                        onChange={(e) => setEditedValue(e.target.value)}
                        style={{ marginLeft: "10px" }}
                      />
                    </div>
                    <div>
                      <button onClick={saveEditedPlaceholder} style={{ marginRight: "5px" }}>
                        Save
                      </button>
                      <button onClick={() => setEditKey(null)}>Cancel</button>
                    </div>
                  </>
                ) : (
                  <>
                    <div style={{ flex: "1" }}>
                      <strong>{original}</strong>: {placeholder}
                    </div>
                    <div>
                      <FontAwesomeIcon
                        icon={faEdit}
                        onClick={() => handleEditPlaceholder(original)}
                        style={{
                          cursor: "pointer",
                          marginRight: "10px",
                          color: "#007bff",
                        }}
                        title="Edit Placeholder"
                      />
                      <FontAwesomeIcon
                        icon={faTrashAlt}
                        onClick={() => handleRemovePlaceholder(original)}
                        className="destructive-icon"
                        style={{ cursor: "pointer", marginRight: "10px" }}
                        title="Remove Placeholder"
                      />
                      <FontAwesomeIcon
                        icon={faArrowCircleRight}
                        onClick={() => handleConvertToWhitelist(original)}
                        style={{ cursor: "pointer", color: "green", marginRight: "10px" }}
                        title="Add to Whitelist"
                      />
                      <FontAwesomeIcon
                        icon={faSyncAlt}
                        onClick={() => regeneratePlaceholder(original)}
                        style={{ cursor: "pointer", color: "#ff9800" }}
                        title="Regenerate Placeholder"
                      />
                    </div>
                  </>
                )}
              </li>
            ))}
        </ul>
      )}
      {/* Non-Name Whitelist */}
      <h2>Non-Name Whitelist</h2>
      {nonWordWhiteList.length === 0 ? (
        <p>No items in the whitelist.</p>
      ) : (
        <ul style={{ listStyleType: "none", padding: "0" }}>
          {nonWordWhiteList
            .slice()
            .sort((a, b) => a.localeCompare(b)) // Sort whitelist items alphabetically
            .map((item, index) => (
              <li
                key={index}
                className="list-item"
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  padding: "8px",
                  borderBottom: "1px solid #ddd",
                }}
              >
                {item}
                <FontAwesomeIcon
                  icon={faTrashAlt}
                  onClick={() => handleRemoveFromWhitelist(item)}
                  className="destructive-icon"
                  style={{ cursor: "pointer", marginLeft: "10px" }}
                  title="Remove from Whitelist"
                />
              </li>
            ))}
        </ul>
      )}
      {/* Import and Export Buttons */}
      <div className="button-group">
        <button
          onClick={handleExportSettings}
          className="small-button"
          style={{ marginRight: "10px" }}
        >
          Export Settings
        </button>
        <button onClick={handleImportSettings} className="small-button">
          Import Settings
        </button>
        {/* Hidden file input */}
        <input
          type="file"
          accept="application/json"
          style={{ display: "none" }}
          ref={fileInputRef}
          onChange={handleFileSelect}
        />
      </div>

      {/* Anonymization Settings Modal */}
      {isSettingsModalOpen && (
        <div className="modal-overlay">
          <div className="modal-content">
            <FontAwesomeIcon icon={faTimes} onClick={() => setIsSettingsModalOpen(false)} className="modal-close-icon" />
            <div className="anonymization-settings">
              <h2>Anonymization Settings</h2>
              {/* Phone Number Anonymization */}
              <div style={{ marginBottom: "10px" }}>
                <h3>Phone Number Anonymization</h3>
                <label style={{ marginRight: "15px" }}>
                  <input
                    type="radio"
                    name="phoneAnonymization"
                    value="placeholder"
                    checked={anonymizePhoneNumberType === 'placeholder'}
                    onChange={(e) => setAnonymizePhoneNumberType(e.target.value)}
                  />
                  Random Placeholder
                </label>
                <label>
                  <input
                    type="radio"
                    name="phoneAnonymization"
                    value="lastThree"
                    checked={anonymizePhoneNumberType === 'lastThree'}
                    onChange={(e) => setAnonymizePhoneNumberType(e.target.value)}
                  />
                  Last three digits (xxxx-xxx-YYY)
                </label>
              </div>

              {/* Email Anonymization */}
              <div style={{ marginBottom: "10px" }}>
                <h3>Email Anonymization</h3>
                <label style={{ marginRight: "15px" }}>
                  <input
                    type="radio"
                    name="emailAnonymization"
                    value="placeholder"
                    checked={anonymizeEmailType === 'placeholder'}
                    onChange={(e) => setAnonymizeEmailType(e.target.value)}
                  />
                  Random Placeholder
                </label>
                <label>
                  <input
                    type="radio"
                    name="emailAnonymization"
                    value="redactFullEmail"
                    checked={anonymizeEmailType === 'redactFullEmail'}
                    onChange={(e) => setAnonymizeEmailType(e.target.value)}
                  />
                  Replace with [email protected]
                </label>
              </div>

              {/* Name Anonymization */}
              <div>
                <h3>Name Anonymization</h3>
                <label style={{ marginRight: "15px" }}>
                  <input
                    type="radio"
                    name="nameAnonymization"
                    value="placeholder"
                    checked={anonymizeNameType === 'placeholder'}
                    onChange={(e) => setAnonymizeNameType(e.target.value)}
                  />
                  Random Placeholder
                </label>
                <label>
                  <input
                    type="radio"
                    name="nameAnonymization"
                    value="firstNameInitial"
                    checked={anonymizeNameType === 'firstNameInitial'}
                    onChange={(e) => setAnonymizeNameType(e.target.value)}
                  />
                  First name & Last initial (e.g., Chris F.)
                </label>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* More Information Modal */}
      {isInfoModalOpen && (
        <div className="modal-overlay">
          <div className="modal-content">
            <FontAwesomeIcon icon={faTimes} onClick={() => setIsInfoModalOpen(false)} className="modal-close-icon" />
            <h2>About DecoyText</h2>
            <div style={{ marginTop: "20px", fontSize: "1rem", color: "#333" }}>
              <p>
                This tool is designed to replace potentially sensitive information in a block of text
                with realistic placeholders. It detects names, company names, email addresses, phone numbers,
                and other identifiable data, replacing them with placeholders while preserving the format of the input.
              </p>
              <p>
                Additionally, you can manually add custom placeholder mappings for specific terms like company names
                or other values that aren't automatically detected.
              </p>
              <p>
                The processed text can be safely used in generative AI tools like ChatGPT, ensuring sensitive content remains protected.
                If you later receive an AI-generated response with placeholders, you can reverse the placeholders back to their original values.
              </p>
              <p>
                <strong>Usage:</strong>
              </p>
              <ol className="description-list" style={{paddingLeft: '20px'}}>
                <li>Paste or enter your text in the original input box below.</li>
                <li>
                  Click <strong>"Convert Placeholders"</strong> to replace sensitive information with realistic placeholders.
                </li>
                <li>
                  Optionally, add custom placeholder mappings for your own specified terms
                  (e.g., company names).
                </li>
                <li>
                  Now, you can use the processed placeholder text within a generative AI tool like ChatGPT.
                  Let the AI generate its response based on the redacted data.
                </li>
                <li>
                  Once you receive a response containing placeholders, paste it into the converted text box and click
                  <strong>"Reverse Placeholders"</strong> to restore the original values.
                </li>
              </ol>
              {/* Caution Note */}
              <p style={{ color: "red", fontWeight: "bold" }}>
                Note: Automatic detection is not perfect. Please review the processed text carefully for any remaining sensitive information before using it.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PlaceholderTool;